import Wrapper from './wrapper';
import { IWrapper, IFileResult } from './types';
import {
  IDataExportsCreateParams,
  IDataExportsGetByUserResponse,
  IDataExportsGetByUserParams,
} from '../types/my-types';
import {
  PaginatedPackagerResponse,
  PackagerAttributes,
  ChainOfCustodyReportData,
  TemplateReportType,
} from '../types/packager-types';
import { Packager as IPackager } from '../types/packager-types';

class Packager {
  wrapper: IWrapper;
  /**
   * @param {Wrapper} wrapper
   */
  constructor(wrapper: Wrapper) {
    this.wrapper = wrapper;
  }

  retrieve(dataPoolId: string): Promise<PaginatedPackagerResponse> {
    return this.wrapper.client.get(`data-pools/${dataPoolId}/packagers`);
  }

  create(dataPoolId: string, label: string): Promise<PaginatedPackagerResponse> {
    return this.wrapper.client.post(`data-pools/${dataPoolId}/packagers`, { label });
  }

  update(dataPoolId: string, id: string, data: IPackager): Promise<PaginatedPackagerResponse> {
    return this.wrapper.client.patch(`data-pools/${dataPoolId}/packagers/${id}`, data);
  }

  simulate(dataPoolId: string, id: string, data: IPackager): Promise<any> {
    return this.wrapper.client.post(
      `data-pools/${dataPoolId}/packagers/${id}/simulate-package`,
      data
    );
  }

  getAttributes(dataPoolId: string, id: string): Promise<PackagerAttributes> {
    return this.wrapper.client.get(`data-pools/${dataPoolId}/packagers/${id}/available-attributes`);
  }

  chainOfCustodyReport(
    packagerId: string,
    params: {
      format: 'csv' | 'json';
      download: boolean;
      start_date: string;
      end_date: string;
    }
  ): Promise<ChainOfCustodyReportData> {
    return this.wrapper.client.post(
      `data-pools/${this.wrapper.dataPoolId}/packagers/${packagerId}/chain-of-custody-report`,
      params
    );
  }

  async availableColumns(
    dataPoolId: string,
    packagerId: string,
    reportType: string
  ): Promise<{ name: string; type: string; description: string; filter_conditions?: string[]; supports_split_by?: boolean }[]> {
    const response: Record<string, { name: string; type: string; description: string; filter_conditions?: string[]; supports_split_by?: boolean }[]> =
      await this.wrapper.client.get(
        `data-pools/${dataPoolId}/packagers/${packagerId}/available-columns`
      );
    const columns = response[reportType] || [];

    // The activity report merges in mat_ct columns as an enrichment at generation
    // time, so they're absent from the base activity schema the endpoint returns.
    if (reportType === TemplateReportType.ACTIVITY) {
      const seen = new Set(columns.map((c) => c.name));
      const matCtColumns = (response[TemplateReportType.MAT_CT] || []).filter(
        (c) => !seen.has(c.name)
      );
      return [...columns, ...matCtColumns];
    }

    return columns;
  }
}

export default Packager;
